import { useEffect } from 'react';
import {
    Box,
    Button,
    Container,
    Paper,
    Typography,
    CircularProgress,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { useUserRole } from '../context/userRoleContext';
import { useMsal } from '@azure/msal-react';

// Background image options for construction theme
const BACKGROUND_IMAGE = 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=2070&auto=format&fit=crop';

export const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const { userRole, isAdmin } = useUserRole();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { isAuthenticated, isLoading, redirectProcessing, login } = useAuth();
    const { instance } = useMsal();
    const currentAccounts = instance.getAllAccounts();
    
    // Effect to handle redirecting after successful login
    useEffect(() => {
        if (isAuthenticated) {
            // Redirect based on role
            navigate(isAdmin ? '/dashboard' : '/timesheetList');
        }
    }, [isAuthenticated, userRole, isAdmin, navigate]);

    const handleLogin = async () => {
        login();
    };

    // If already authenticated, don't show login page

if (isLoading || redirectProcessing || isAuthenticated || currentAccounts.length > 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: 'calc(100vh - 16px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url(${BACKGROUND_IMAGE})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                padding: 0
            }}
        >
            <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Paper
                        elevation={6}
                        sx={{
                            p: isMobile ? 3 : 5,
                            width: '100%',
                            borderRadius: 2,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)'
                        }}
                    >
                        <Box sx={{ mb: 4, textAlign: 'center' }}>
                            {/* Company logo */}
                            <Box
                                sx={{
                                    mb: 2,
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                            >
                                <img
                                    src="/logo.jpg"
                                    alt="Company Logo"
                                    style={{
                                        maxWidth: '180px',
                                        height: 'auto',
                                        borderRadius: '6px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}
                                />
                            </Box>

                            <Typography
                                component="h1"
                                variant="h4"
                                gutterBottom
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                    mt: 2
                                }}
                            >
                                Bienvenue
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                color="text.secondary"
                                sx={{ mb: 2 }}
                            >
                                Connectez-vous pour accéder à votre espace de travail
                            </Typography>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleLogin}
                                disabled={isLoading}
                                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                                sx={{
                                    mt: 2,
                                    mb: 2,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    borderRadius: 2,
                                    boxShadow: 3,
                                    '&:hover': {
                                        boxShadow: 5,
                                    }
                                }}
                                size="large"
                            >
                                {isLoading ? 'Connexion en cours...' : 'Se connecter'}
                            </Button>
                        </Box>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            align="center"
                            sx={{ mt: 3 }}
                        >
                            Rapport d'heures
                        </Typography>
                    </Paper>
                </Box>
            </Container>
        </Box>
    );
};